// Numeric columns for one Audit trail row.
//
// Kept out of DealOverview.tsx so the decisions here can be tested directly
// rather than inferred from the rendered markup. There are only a few, but each
// one has already been got wrong once:
//
//  - A count of 0 is an answer ("no animals on this event carried an EID"), not
//    a missing value. Falling back with `||` turns it into the previous
//    column's number, which reads as data where there is none.
//  - `rows_in_file` can exceed `total`: a row matching no animal is absent from
//    every other figure on the row. That gap is the interesting part, so it is
//    reported rather than left to be spotted by eye.
//  - `total` is only ever the backend's consistent trio or the event's own
//    animal_count — never `withEid + withoutEid` recomputed here, which would
//    paper over a backend disagreement instead of showing it.

export interface AuditCountSource {
  animals_total_count?: number | string | null;
  animal_count?: number | string | null;
  animals_with_eid_count?: number | string | null;
  animals_without_eid_count?: number | string | null;
  dataset_record_count?: number | string | null;
}

export interface AuditCounts {
  rowsInFile: number | null;
  total: number | null;
  withEid: number | null;
  withoutEid: number | null;
  /** Rows in the file that never became animals on this event; 0 when none. */
  droppedRows: number;
}

export function toCount(value: unknown): number | null {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function auditCounts(evt: AuditCountSource | null | undefined): AuditCounts {
  const source = evt || {};
  const total = toCount(source.animals_total_count) ?? toCount(source.animal_count);
  const rowsInFile = toCount(source.dataset_record_count);
  const droppedRows =
    rowsInFile != null && total != null && rowsInFile > total ? rowsInFile - total : 0;
  return {
    rowsInFile,
    total,
    withEid: toCount(source.animals_with_eid_count),
    withoutEid: toCount(source.animals_without_eid_count),
    droppedRows,
  };
}
